import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Row, Col, Button, Input, notification, Form,
} from 'antd';
import Background from '../sharedComponents/Background/Background';
import logo from '../../assets/images/CooperLogo.png';
import './ResetPassword.scss';
import {
  isSuperAgentError, put, ResponseWrapper, RequestWrapper,
} from '../../dataSource/requests';
import { removeUserFromStorage } from '../../utils/users';

interface IResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

const ResetPassword = (): JSX.Element => {
  const [formInstance] = Form.useForm();
  const { token: tokenParam } = useParams();
  const [token] = useState(tokenParam);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, []);

  const handleConfirmBlur = (): void => {
    // ? componente original continha uma referencia a esta funcao inexistente
  };

  const compareToFirstPassword = (_rule: any, value:string): Promise<void> => {
    if (value && value !== formInstance.getFieldValue('newPassword')) {
      return Promise.reject(new Error('Nova senha e confirmar nova senha devem ser iguais'));
    }

    return Promise.resolve();
  };

  const validateToNextPassword = (_rule: any, value: string): Promise<void> => {
    if (value && value.length < 6) {
      return Promise.reject(new Error('A senha deve ter no mínimo 6 caracteres'));
    }

    return Promise.resolve();
  };

  const handleSubmit = async (values: IResetPasswordForm): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    setLoading(true);
    removeUserFromStorage();

    const requestReference: RequestWrapper = put({
      url: '/auth/user/passwordReset/',

      params: {
        newPassword: values.newPassword,
        token,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log(error);
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    setLoading(false);

    switch (true) {
      case (response?.body?.code === 1008):
        // "Password redefined successfully", 1008
        notification.success({
          message: 'Senha redefinida com sucesso.',
          description: 'Agora você pode fazer login no sistema utilizando sua nova senha.',
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -1009):
        // "Password reset token not found", -1009
        notification.error({
          message: 'Erro.',
          description: 'Token de redefinição de senha não encontrado.',
        });
        break;

      case (responseError?.body?.code === -1005):
        // "Invalid email token", -1005
        notification.error({
          message: 'Erro.',
          description: 'Token de redefinição de senha inválido.',
        });
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Usuario não encontrado.',
          description: 'Verifique e tente novamente.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro.',
          description: 'Verifique e tente novamente.',
        });
        break;
    }
  };

  return (
    !token
      ? (
        <Navigate to="/" />
      )
      : (
        <div className="ResetPassword">
          <Row>
            <Col xs={{ span: 0 }} md={{ span: 14 }} className="ResetPassword__background">
              <Background />
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 10 }} className="ResetPassword__content">
              <div className="ResetPassword__container">
                <img className="ResetPassword__logo-image" src={logo} alt="" />

                <Form
                  scrollToFirstError
                  layout="vertical"
                  className="ResetPassword__form"
                  onFinish={handleSubmit}
                  form={formInstance}
                >
                  <Form.Item
                    label="Nova senha"
                    name="newPassword"
                    rules={[
                      {
                        required: true, message: 'Por favor, insira sua nova senha',
                      },
                      {
                        validator: validateToNextPassword,
                      },
                      {
                        max: 150, message: 'Máximo de 150 caracteres',
                      },
                    ]}
                  >
                    <Input.Password
                      name="password"
                      type="password"
                      placeholder="Nova senha"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Confirmar nova senha"
                    name="confirmPassword"
                    rules={[
                      {
                        required: true,
                        message: 'Por favor, confirme sua nova senha',
                      },
                      {
                        validator: compareToFirstPassword,
                      },
                      {
                        max: 150, message: 'Máximo de 150 caracteres',
                      },
                    ]}
                  >
                    <Input.Password
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirmar nova senha"
                      onBlur={handleConfirmBlur}
                    />
                  </Form.Item>

                  <Form.Item
                    className="ResetPassword__FormItemButton"
                  >
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="ResetPassword__FormButton"
                      loading={loading}
                    >
                      Salvar
                    </Button>
                  </Form.Item>
                  <Button
                    className="ResetPassword__voltar"
                    type="link"
                    onClick={
                      (): void => {
                        navigate('/');
                      }
                    }
                  >
                    voltar para o login
                  </Button>
                </Form>
              </div>
            </Col>
          </Row>
        </div>
      )
  );
};

ResetPassword.propTypes = {};

ResetPassword.defaultProps = {
};

export default ResetPassword;
